// Utilities (from React code) for shallow comparison of objects.

const hasOwnProperty = Object.prototype.hasOwnProperty;

/*
 * inlined Object.is polyfill to avoid requiring consumers ship their own
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
 */
const is = (objA, objB) => {
  // SameValue algorithm
  if (objA === objB) {
    // Steps 1-5, 7-10
    // Steps 6.b-6.e: +0 != -0
    // Added the nonzero objB check to make Flow happy, but it is redundant
    return objA !== 0 || objB !== 0 || 1 / objA === 1 / objB;
  }
  // Step 6.a: NaN == NaN
  return objA !== objA && objB !== objB;  // eslint-disable-line no-self-compare
};

/*
 * Performs equality by iterating through keys on an object and returning false
 * when any key has values which are not strictly equal between the arguments.
 * Returns true when the values of all keys are strictly equal.
 */
export const shallowEqual = (objA, objB) => {
  if (is(objA, objB)) {
    return true;
  }

  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  // Test for A's keys different from B.
  for (let i = 0; i < keysA.length; i++) {  // eslint-disable-line id-length, no-plusplus
    if (!hasOwnProperty.call(objB, keysA[i]) || !is(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }

  return true;
};

// Like shallowEqual() but verifies nested properties.
export const deepShallowEqual = (objA, objB) => {
  if (is(objA, objB)) {
    return true;
  }

  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  // Test for A's keys different from B.
  for (let i = 0; i < keysA.length; i++) {  // eslint-disable-line id-length, no-plusplus
    if (!hasOwnProperty.call(objB, keysA[i]) || !deepShallowEqual(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }

  return true;
};
